import { useState, useEffect, useRef } from "react";
import { Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Download } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import visotonicsLogo from "../assets/images/logo.png";

const RATE_CARD = {
  "CORNER POST CUT": { base: 12000, perCm2: 1.8 },
  CRACK: { base: 4500, perCm2: 1.2 },
  DEFORMATION: { base: 6000, perCm2: 1.4 },
  "DENT - MAJOR": { base: 2500, perCm2: 0.85 },
  "DENT - MINOR": { base: 1200, perCm2: 0.45 },
  "DENT AND RUST": { base: 2800, perCm2: 0.95 },
  "RUST - MAJOR": { base: 2200, perCm2: 0.7 },
  "RUST - MINOR": { base: 900, perCm2: 0.3 },
  "RUST - UPPER": { base: 1100, perCm2: 0.38 },
  "RUST - FRONT": { base: 1100, perCm2: 0.38 },
  "RUST - BACK DOOR": { base: 1300, perCm2: 0.42 },
  OTHER: { base: 1200, perCm2: 0.5 },
};

const getRate = (name) => RATE_CARD[name?.toUpperCase()] ?? RATE_CARD["OTHER"];
const calcTotal = (area, base, perCm2) => base + area * perCm2;
const TABLE_HEADERS = [
  "#",
  "Defect Type",
  "Area (cm²)",
  "Base Price (₹)",
  "Rate (₹/cm²)",
  "Total (₹)",
];
const ASSET_TYPE_LABELS = {
  Container: "Container Number",
  IsoTank: "Tank Number",
  Other: "Asset Number",
};

const fmt = (n) =>
  Number(n.toFixed(2)).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// ── All sub-components defined OUTSIDE main component ──────────────────────

function EditableNumberInput({ value, step, width, onChange }) {
  const [local, setLocal] = useState(String(value));

  useEffect(() => {
    setLocal(String(value));
  }, [value]);

  return (
    <input
      type="number"
      min="0"
      step={step}
      value={local}
      onChange={(e) => {
        const raw = e.target.value;
        setLocal(raw);
        const parsed = parseFloat(raw);
        if (!isNaN(parsed) && parsed >= 0) {
          onChange(parsed);
        }
      }}
      onBlur={() => {
        const parsed = parseFloat(local);
        const safe = isNaN(parsed) || parsed < 0 ? 0 : parsed;
        setLocal(String(safe));
        onChange(safe);
      }}
      style={{
        width,
        textAlign: "center",
        border: "1.5px solid #3b82f6",
        borderRadius: 6,
        padding: "4px 6px",
        fontSize: 12,
        outline: "none",
        background: "#eff6ff",
        color: "#1e40af",
        fontWeight: 600,
      }}
    />
  );
}

// ── CompanyLogo — handles broken/missing logo gracefully ──────────────────
function CompanyLogo({ src, height, maxWidth }) {
  const [failed, setFailed] = useState(false);

  // Reset failed state when src changes
  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!src || failed) return null;

  return (
    <img
      src={src}
      alt="Company"
      onError={() => setFailed(true)}
      style={{
        height,
        width: "auto",
        maxWidth: maxWidth || 160,
        objectFit: "contain",
      }}
    />
  );
}

function HeaderBar({ forPdf, uiLogoSrc, pdfLogoSrc }) {
  const h = forPdf ? 40 : 36;
  const pad = forPdf ? "16px 40px 0 40px" : "16px 24px";
  const dividerMargin = forPdf ? "10px 40px 0 40px" : "10px 24px 0 24px";
  const fontSize = forPdf ? 10 : 11;
  const logoSrc = forPdf ? pdfLogoSrc : uiLogoSrc;

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: pad,
        }}
      >
        <img
          src={visotonicsLogo}
          alt="Visotonics"
          style={{ height: h, width: "auto", objectFit: "contain" }}
        />
        {/* Company logo — shows nothing if missing or broken */}
        <CompanyLogo src={logoSrc} height={h} maxWidth={160} />
        {/* Fallback contact info only if no logo src at all */}
        {!logoSrc && (
          <div style={{ textAlign: "right" }}>
            <p
              style={{
                fontSize,
                fontWeight: "bold",
                color: "#475569",
                margin: 0,
              }}
            >
              https://www.visotonics.com/
            </p>
            <p
              style={{
                fontSize,
                fontWeight: "bold",
                color: "#475569",
                margin: 0,
              }}
            >
              +91-9651891556
            </p>
          </div>
        )}
      </div>
      <div style={{ height: 2, background: "#000", margin: dividerMargin }} />
    </>
  );
}

function MetaBlock({ reportData }) {
  const assetType = localStorage.getItem("asset_type") || "Container";
  const assetLabel =
    ASSET_TYPE_LABELS[assetType] || ASSET_TYPE_LABELS["Container"];
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 20,
        flexWrap: "wrap",
        gap: 8,
      }}
    >
      <div>
        <p
          style={{
            fontSize: 20,
            fontWeight: 800,
            color: "#1e293b",
            marginBottom: 4,
          }}
        >
          Repair Estimate Quotation
        </p>
        <p style={{ fontSize: 11, color: "#64748b", marginBottom: 2 }}>
          Report No: <b>{reportData.report_number || "-"}</b>
        </p>
        <p style={{ fontSize: 11, color: "#64748b" }}>
          {assetLabel}: <b>{reportData.container_number || "-"}</b>
        </p>
      </div>
      <div style={{ textAlign: "right" }}>
        <p style={{ fontSize: 11, color: "#64748b", marginBottom: 2 }}>
          Company: <b>{reportData.company_name || "-"}</b>
        </p>
        <p style={{ fontSize: 11, color: "#64748b", marginBottom: 2 }}>
          Yard: <b>{reportData.yard_name || "-"}</b>
        </p>
        <p style={{ fontSize: 11, color: "#64748b" }}>
          Date: <b>{reportData.created_at || "-"}</b>
        </p>
      </div>
    </div>
  );
}

function SummaryBlock({
  editable,
  subTotal,
  gst,
  gstAmount,
  grandTotal,
  onGstChange,
}) {
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
      <table style={{ width: 300, borderCollapse: "collapse", fontSize: 12 }}>
        <tbody>
          <tr>
            <td
              style={{
                padding: "8px 14px",
                color: "#64748b",
                border: "1px solid #e2e8f0",
              }}
            >
              Sub Total
            </td>
            <td
              style={{
                padding: "8px 14px",
                textAlign: "right",
                border: "1px solid #e2e8f0",
                fontWeight: 600,
              }}
            >
              ₹ {fmt(subTotal)}
            </td>
          </tr>
          <tr style={{ background: "#f8fafc" }}>
            <td
              style={{
                padding: "8px 14px",
                color: "#64748b",
                border: "1px solid #e2e8f0",
              }}
            >
              {editable ? (
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  GST
                  <EditableNumberInput
                    value={gst}
                    step={1}
                    width={46}
                    onChange={onGstChange}
                  />
                  %
                </span>
              ) : (
                `GST (${gst}%)`
              )}
            </td>
            <td
              style={{
                padding: "8px 14px",
                textAlign: "right",
                border: "1px solid #e2e8f0",
              }}
            >
              ₹ {fmt(gstAmount)}
            </td>
          </tr>
          <tr style={{ background: "#1e293b" }}>
            <td
              style={{
                padding: "10px 14px",
                color: "#fff",
                fontWeight: 700,
                border: "1px solid #1e293b",
              }}
            >
              Grand Total
            </td>
            <td
              style={{
                padding: "10px 14px",
                textAlign: "right",
                color: "#fff",
                fontWeight: 800,
                fontSize: 14,
                border: "1px solid #1e293b",
              }}
            >
              ₹ {fmt(grandTotal)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function DefectTableRows({ forPdf, rows, onBaseChange, onPerCm2Change }) {
  return (
    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        fontSize: forPdf ? 11 : 12,
      }}
    >
      <thead>
        <tr style={{ background: "#f1f5f9" }}>
          {TABLE_HEADERS.map((h) => (
            <th
              key={h}
              style={{
                padding: forPdf ? "9px 10px" : "10px 12px",
                textAlign: h === "Defect Type" ? "left" : "center",
                fontWeight: 700,
                color: "#475569",
                fontSize: forPdf ? 10 : 11,
                border: "1px solid #e2e8f0",
                whiteSpace: "nowrap",
              }}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
            <td
              style={{
                padding: forPdf ? "8px 10px" : "8px 12px",
                textAlign: "center",
                border: "1px solid #e2e8f0",
                color: "#94a3b8",
                fontSize: 11,
              }}
            >
              {row.defect_no}
            </td>
            <td
              style={{
                padding: forPdf ? "8px 10px" : "8px 12px",
                border: "1px solid #e2e8f0",
                fontWeight: 600,
                color: "#1e293b",
              }}
            >
              {row.defect_name}
            </td>
            <td
              style={{
                padding: forPdf ? "8px 10px" : "8px 12px",
                textAlign: "center",
                border: "1px solid #e2e8f0",
              }}
            >
              {row.area.toFixed(2)}
            </td>
            {forPdf ? (
              <>
                <td
                  style={{
                    padding: "8px 10px",
                    textAlign: "center",
                    border: "1px solid #e2e8f0",
                    fontWeight: 600,
                  }}
                >
                  ₹ {row.base.toLocaleString("en-IN")}
                </td>
                <td
                  style={{
                    padding: "8px 10px",
                    textAlign: "center",
                    border: "1px solid #e2e8f0",
                    fontWeight: 600,
                  }}
                >
                  {row.perCm2.toFixed(2)}
                </td>
              </>
            ) : (
              <>
                <td
                  style={{
                    padding: "4px 8px",
                    textAlign: "center",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <EditableNumberInput
                    value={row.base}
                    step={100}
                    width={90}
                    onChange={(val) => onBaseChange(i, val)}
                  />
                </td>
                <td
                  style={{
                    padding: "4px 8px",
                    textAlign: "center",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <EditableNumberInput
                    value={row.perCm2}
                    step={0.05}
                    width={80}
                    onChange={(val) => onPerCm2Change(i, val)}
                  />
                </td>
              </>
            )}
            <td
              style={{
                padding: forPdf ? "8px 10px" : "8px 12px",
                textAlign: "center",
                border: "1px solid #e2e8f0",
                fontWeight: 700,
                color: "#16a34a",
              }}
            >
              ₹ {fmt(calcTotal(row.area, row.base, row.perCm2))}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Main component ──────────────────────────────────────────────────────────

export default function RepairEstimateModal({
  open,
  onClose,
  defects = [],
  reportData = {},
}) {
  const [rows, setRows] = useState([]);
  const [gst, setGst] = useState(18);
  const [downloading, setDownloading] = useState(false);
  const pdfRef = useRef(null);

  const companyLogoUrl = localStorage.getItem("company_logo") || null;
  const companyLogoBase64 = localStorage.getItem("company_logo_base64") || null;
  const pdfLogoSrc = companyLogoBase64 || null;
  const uiLogoSrc = companyLogoUrl || null;

  useEffect(() => {
    if (!open) return;
    const filtered = defects
      .filter(
        (d) => d.is_significant_defect && d.defect_dimension?.area_cm2 > 0,
      )
      .sort((a, b) =>
        a.image_no !== b.image_no
          ? a.image_no - b.image_no
          : a.defect_no - b.defect_no,
      );

    // ── Group by image_no + defect_name: one base per class per image ──
    const groupMap = {};
    filtered.forEach((d) => {
      const key = `${d.image_no}_${d.defect_name?.toUpperCase() || "OTHER"}`;
      const rate = getRate(d.defect_name);
      if (!groupMap[key]) {
        groupMap[key] = {
          image_no: d.image_no,
          defect_name: d.defect_name,
          area: 0,
          base: rate.base,
          perCm2: rate.perCm2,
        };
      }
      groupMap[key].area += d.defect_dimension.area_cm2;
    });

    // Sort by image_no then defect_name
    const grouped = Object.values(groupMap).sort((a, b) =>
      a.image_no !== b.image_no
        ? a.image_no - b.image_no
        : a.defect_name.localeCompare(b.defect_name),
    );

    setRows(
      grouped.map((r, i) => ({
        ...r,
        defect_no: i + 1,
        area: r.area,
      })),
    );
    setGst(18);
  }, [open, defects]);

  const handleBaseChange = (i, val) =>
    setRows((prev) =>
      prev.map((r, idx) => (idx === i ? { ...r, base: val } : r)),
    );
  const handlePerCm2Change = (i, val) =>
    setRows((prev) =>
      prev.map((r, idx) => (idx === i ? { ...r, perCm2: val } : r)),
    );

  const subTotal = rows.reduce(
    (s, r) => s + calcTotal(r.area, r.base, r.perCm2),
    0,
  );
  const gstAmount = subTotal * (gst / 100);
  const grandTotal = subTotal + gstAmount;

  const handleDownload = async () => {
    if (!pdfRef.current) return;
    setDownloading(true);
    try {
      await new Promise((r) => setTimeout(r, 100));
      const canvas = await html2canvas(pdfRef.current, {
        scale: 2,
        useCORS: false,
        allowTaint: false,
        backgroundColor: "#ffffff",
        logging: false,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgW = pageW;
      const imgH = (canvas.height * pageW) / canvas.width;

      let yOffset = 0,
        remaining = imgH;
      while (remaining > 0) {
        pdf.addImage(imgData, "PNG", 0, -yOffset, imgW, imgH);
        remaining -= pageH;
        yOffset += pageH;
        if (remaining > 0) pdf.addPage();
      }
      pdf.save(
        `Repair_Estimate_${reportData.container_number || "estimate"}.pdf`,
      );
    } catch (err) {
      console.error("PDF generation failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: "14px", maxHeight: "95vh" } }}
    >
      <DialogTitle
        sx={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9" }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: 15, fontWeight: 700, color: "#1e293b" }}>
            🔧 Repair Estimate Quotation
          </span>
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent sx={{ padding: "20px", background: "#f8fafc" }}>
        {rows.length === 0 ? (
          <p
            style={{
              textAlign: "center",
              color: "#94a3b8",
              padding: "40px 0",
              fontSize: 13,
            }}
          >
            No significant defects with area data to estimate.
          </p>
        ) : (
          <>
            {/* ── VISIBLE EDITABLE UI ─────────────────────────── */}
            <div
              style={{
                background: "#fff",
                borderRadius: 10,
                overflow: "hidden",
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
              }}
            >
              <HeaderBar
                forPdf={false}
                uiLogoSrc={uiLogoSrc}
                pdfLogoSrc={pdfLogoSrc}
              />
              <div style={{ padding: 24 }}>
                <MetaBlock reportData={reportData} />
                <div
                  style={{ height: 1, background: "#e2e8f0", marginBottom: 16 }}
                />
                <div style={{ overflowX: "auto" }}>
                  <DefectTableRows
                    forPdf={false}
                    rows={rows}
                    onBaseChange={handleBaseChange}
                    onPerCm2Change={handlePerCm2Change}
                  />
                </div>
                <SummaryBlock
                  editable={true}
                  subTotal={subTotal}
                  gst={gst}
                  gstAmount={gstAmount}
                  grandTotal={grandTotal}
                  onGstChange={setGst}
                />
                <p style={{ fontSize: 10, color: "#94a3b8", marginTop: 16 }}>
                  Note: This is a system-generated estimate. Final charges may
                  vary based on site conditions.
                </p>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: 20,
                  }}
                >
                  <button
                    onClick={handleDownload}
                    disabled={downloading}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 24px",
                      borderRadius: 8,
                      border: "none",
                      background: downloading ? "#d1d5db" : "#16a34a",
                      color: "#fff",
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: downloading ? "not-allowed" : "pointer",
                      boxShadow: downloading
                        ? "none"
                        : "0 2px 8px rgba(22,163,74,0.3)",
                    }}
                  >
                    <Download size={15} />
                    {downloading
                      ? "Generating PDF..."
                      : "Generate & Download PDF"}
                  </button>
                </div>
              </div>
            </div>

            {/* ── HIDDEN CLEAN PDF VERSION ────────────────────── */}
            <div
              ref={pdfRef}
              style={{
                position: "fixed",
                top: -9999,
                left: -9999,
                width: 794,
                background: "#fff",
                fontFamily: "Arial, sans-serif",
              }}
            >
              <HeaderBar
                forPdf={true}
                uiLogoSrc={uiLogoSrc}
                pdfLogoSrc={pdfLogoSrc}
              />
              <div style={{ padding: "20px 40px 40px 40px" }}>
                <MetaBlock reportData={reportData} />
                <div
                  style={{ height: 1, background: "#e2e8f0", marginBottom: 16 }}
                />
                <DefectTableRows
                  forPdf={true}
                  rows={rows}
                  onBaseChange={null}
                  onPerCm2Change={null}
                />
                <SummaryBlock
                  editable={false}
                  subTotal={subTotal}
                  gst={gst}
                  gstAmount={gstAmount}
                  grandTotal={grandTotal}
                  onGstChange={null}
                />
                <p style={{ fontSize: 9, color: "#94a3b8", marginTop: 12 }}>
                  Note: This is a system-generated estimate. Final charges may
                  vary based on site conditions.
                </p>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
